import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { 
    MagnifyingGlassIcon, 
    ChevronUpIcon, 
    ChevronDownIcon,
    XMarkIcon
} from '@heroicons/react/20/solid'
import { Cog6ToothIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline'
import { equipmentMedia } from 'functions/equipmentUtils'
import { GetProgressionLevelName } from 'functions/textUtils'

import ScreenFrame from 'components/Frame/ScreenFrame'
import MediumLoadingComponent from 'components/Loading/MediumLoadingComponent'

// Custom dropdown component

export default function ExerciseDirectory() {
    const navigate = useNavigate()
    const { exercises } = useGetExercises()
    const [showCopyToast, setShowCopyToast] = useState(false)
    const [copyToastText, setCopyToastText] = useState('')

    const GetProgressionLevelColor = (joinedProgressionLevel) => {

        if (joinedProgressionLevel.startsWith('0')) return 'bg-orange-100 '
        else if (joinedProgressionLevel.startsWith('1')) return 'bg-orange-200 '
        else if (joinedProgressionLevel.startsWith('2')) return 'bg-orange-300 '
        else if (joinedProgressionLevel.startsWith('3')) return 'bg-orange-400 '
        else if (joinedProgressionLevel.startsWith('4')) return 'bg-orange-500 '
        else return 'bg-orange-100 '

    }
    
    // Get unique categories from exercise data
    const categories = useMemo(() => {
        if (!exercises.length) return []
        const uniqueCategories = [...new Set(exercises.map(exercise => exercise.category))]
        return uniqueCategories.sort()
    }, [exercises])

    // Get unique locations from exercise data
    const locations = useMemo(() => {
        if (!exercises.length) return []
        const uniqueLocations = [...new Set(exercises.map(exercise => exercise.location))]
        return uniqueLocations.sort()
    }, [exercises])

    // Get unique laterals from exercise data
    const laterals = useMemo(() => {
        if (!exercises.length) return []
        const uniqueLaterals = [...new Set(exercises.map(exercise => exercise.lateral))]
        return uniqueLaterals.sort()
    }, [exercises])

    // Get unique equipment items from exercise data, splitting CSV strings
    const equipmentItems = useMemo(() => {
        if (!exercises.length) return []
        const allEquipment = exercises
            .map(exercise => exercise.equipment.split(',').map(item => item.trim()))
            .flat()
            .filter(item => item.length > 0)
        const uniqueEquipment = [...new Set(allEquipment)]
        return uniqueEquipment.sort()
    }, [exercises])
    
    // Get unique types from exercise data
    const types = useMemo(() => {
        if (!exercises.length) return []
        const uniqueTypes = [...new Set(exercises.map(exercise => exercise.exerciseType))]
        return uniqueTypes.sort()
    }, [exercises])

    // Get unique equipment quantities from exercise data
    const quantities = useMemo(() => {
        if (!exercises.length) return []
        const uniqueQuantities = [...new Set(exercises.map(exercise => exercise.equipmentQuantity))]
        return uniqueQuantities.sort((a, b) => a - b)
    }, [exercises])

    // Search states
    const [idFilter, setIdFilter] = useState('')
    const [nameFilter, setNameFilter] = useState('')
    const [typeFilter, setTypeFilter] = useState('all')
    const [categoryFilter, setCategoryFilter] = useState('all')
    const [locationFilter, setLocationFilter] = useState('all')
    const [lateralFilter, setLateralFilter] = useState('all')
    const [equipmentFilter, setEquipmentFilter] = useState('all')
    const [quantityFilter, setQuantityFilter] = useState('all')

    // Add sort state
    const [sortConfig, setSortConfig] = useState({
        key: 'id',
        direction: 'desc'
    })

    // Add archived filter state
    const [archivedFilter, setArchivedFilter] = useState(0) // Default to active (0)
    const [excludeFromGeneratorFilter, setExcludeFromGeneratorFilter] = useState('all') // Default to all
    const [customFilter, setCustomFilter] = useState('all') // Default to all

    // Add dropdown visibility states
    const [openFilter, setOpenFilter] = useState(null)

    // Add dropdown position state
    const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 })

    // Handle sort click
    const handleSort = (key) => {
        setSortConfig(current => {
            if (current.key === key) {
                // If clicking the same column, toggle direction
                return {
                    key,
                    direction: current.direction === 'asc' ? 'desc' : 'asc'
                }
            }
            // If clicking a new column, default to ascending
            return {
                key,
                direction: 'asc'
            }
        })
    }

    // Sort exercises based on current sort configuration
    const sortedExercises = useMemo(() => {
        return [...exercises].sort((a, b) => {
            if (sortConfig.key === 'id') {
                return sortConfig.direction === 'asc' 
                    ? a.id - b.id 
                    : b.id - a.id
            }
            if (sortConfig.key === 'name') {
                return sortConfig.direction === 'asc'
                    ? a.name.localeCompare(b.name)
                    : b.name.localeCompare(a.name)
            }
            if (sortConfig.key === 'progressionLevel') {
                return sortConfig.direction === 'asc'
                    ? a.joinedProgressionLevel.localeCompare(b.joinedProgressionLevel)
                    : b.joinedProgressionLevel.localeCompare(a.joinedProgressionLevel)
            }
            return 0
        })
    }, [exercises, sortConfig])

    const OnExerciseNameCopyButtonClicked = (text) => {
        navigator.clipboard.writeText(text);
        setCopyToastText(text);
        setShowCopyToast(true);
        setTimeout(() => setShowCopyToast(false), 2000);
    }

    const OnExerciseClicked = (exercise) => {
        const props = {
            state: {
                exercise: exercise,
            },
        }
        navigate('/exerciseData', props)
    }

    // Filter exercises based on all search terms
    const filteredExercises = sortedExercises.filter(exercise => 
        (idFilter === '' || exercise.id.toString().includes(idFilter)) &&
        exercise.name.toLowerCase().includes(nameFilter.toLowerCase()) &&
        exercise.archived === archivedFilter &&
        (excludeFromGeneratorFilter === 'all' || exercise.excludeFromGenerator === Number(excludeFromGeneratorFilter)) &&
        (customFilter === 'all' || (customFilter === 'custom' && exercise.tags === 'custom')) &&
        (typeFilter === 'all' || exercise.exerciseType === typeFilter) &&
        (categoryFilter === 'all' || exercise.category === categoryFilter) &&
        (locationFilter === 'all' || exercise.location === locationFilter) &&
        (lateralFilter === 'all' || exercise.lateral === lateralFilter) &&
        (equipmentFilter === 'all' || (equipmentFilter === 'none' ? !exercise.equipment : exercise.equipment.split(',').map(e => e.trim()).includes(equipmentFilter))) &&
        (quantityFilter === 'all' || exercise.equipmentQuantity === quantityFilter)
    )

    // Handle filter click
    const handleFilterClick = (filterName, event) => {
        event.stopPropagation() // Prevent sort trigger

        if (openFilter === filterName) {
            setOpenFilter(null)
        } else {
            const rect = event.currentTarget.getBoundingClientRect()
            setDropdownPosition({
                x: rect.right - 192, // 192px = width of dropdown (w-48)
                y: rect.bottom + 4
            })
            setOpenFilter(filterName)
        }
    }

    // Close filter when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setOpenFilter(null)
        document.addEventListener('click', handleClickOutside)
        return () => document.removeEventListener('click', handleClickOutside)
    }, [])

    return (
        <ScreenFrame limitWidth={false}>

            {/* Copy Toast Notification */}
            {showCopyToast && (
                <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg z-50 animate-fade-in-out">
                    Copied "{copyToastText}" to clipboard
                </div>
            )}

            {/* Filter Dropdowns */}
            {openFilter && (
                <div 
                    className="fixed z-50 w-48 rounded-md bg-white shadow-xl ring-1 ring-gray-900/10 border border-gray-300 overflow-y-auto max-h-[600px]"
                    style={{
                        left: `${dropdownPosition.x}px`,
                        top: `${dropdownPosition.y}px`
                    }}
                >
                    <div className="py-1 text-normal">
                        {openFilter === 'type' && (
                            <>
                                <div 
                                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${typeFilter === 'all' ? 'bg-gray-50' : ''}`}
                                    onClick={() => {
                                        setTypeFilter('all')
                                        setOpenFilter(null)
                                    }}
                                >
                                    All Types
                                </div>

                                {types.map(type => (
                                    <div
                                        key={type}
                                        className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${typeFilter === type ? 'bg-gray-50' : ''}`}
                                        onClick={() => {
                                            setTypeFilter(type)
                                            setOpenFilter(null)
                                        }}
                                    >
                                        {type}
                                    </div>
                                ))}
                            </>
                        )}

                        {openFilter === 'category' && (
                            <>
                                <div 
                                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${categoryFilter === 'all' ? 'bg-gray-50' : ''}`}
                                    onClick={() => {
                                        setCategoryFilter('all')
                                        setOpenFilter(null)
                                    }}
                                >
                                    All Categories
                                </div>
                                {categories.map(category => (
                                    <div
                                        key={category}
                                        className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${categoryFilter === category ? 'bg-gray-50' : ''}`}
                                        onClick={() => {
                                            setCategoryFilter(category)
                                            setOpenFilter(null)
                                        }}
                                    >
                                        {category}
                                    </div>
                                ))}
                            </>
                        )}

                        {openFilter === 'location' && (
                            <>
                                <div 
                                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${locationFilter === 'all' ? 'bg-gray-50' : ''}`}
                                    onClick={() => {
                                        setLocationFilter('all')
                                        setOpenFilter(null)
                                    }}
                                >
                                    All Locations
                                </div>

                                {locations.map(location => (
                                    <div
                                        key={location}
                                        className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${locationFilter === location ? 'bg-gray-50' : ''}`}
                                        onClick={() => {
                                            setLocationFilter(location)
                                            setOpenFilter(null)
                                        }}
                                    >
                                        {location}
                                    </div>
                                ))}
                            </>
                        )}
                        
                        {openFilter === 'lateral' && (
                            <>
                                <div 
                                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${lateralFilter === 'all' ? 'bg-gray-50' : ''}`}
                                    onClick={() => {
                                        setLateralFilter('all')
                                        setOpenFilter(null)
                                    }}
                                >
                                    All Laterals
                                </div>

                                {laterals.map(lateral => (
                                    <div
                                        key={lateral}
                                        className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${lateralFilter === lateral ? 'bg-gray-50' : ''}`}
                                        onClick={() => {
                                            setLateralFilter(lateral)
                                            setOpenFilter(null)
                                        }}
                                    >
                                        {lateral}
                                    </div>
                                ))}
                            </>
                        )}

                        {openFilter === 'equipment' && (
                            <>
                                <div 
                                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${equipmentFilter === 'all' ? 'bg-gray-50' : ''}`}
                                    onClick={() => {
                                        setEquipmentFilter('all')
                                        setOpenFilter(null)
                                    }}
                                >
                                    All Equipment
                                </div>

                                <div 
                                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${equipmentFilter === 'none' ? 'bg-gray-50' : ''}`}
                                    onClick={() => {
                                        setEquipmentFilter('none')
                                        setOpenFilter(null)
                                    }}
                                >
                                    No Equipment
                                </div>

                                {equipmentItems.map(equipment => (
                                    <div
                                        key={equipment}
                                        className={`px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2 ${equipmentFilter === equipment ? 'bg-gray-50' : ''}`}
                                        onClick={() => {
                                            setEquipmentFilter(equipment)
                                            setOpenFilter(null)
                                        }}
                                    >
                                        {equipmentMedia[equipment.toLowerCase().replace(/\s+/g, '-')] && (
                                            <img
                                                className="w-5 h-5 object-contain"
                                                src={equipmentMedia[equipment.toLowerCase().replace(/\s+/g, '-')].imageURL}
                                                alt={equipment}
                                            />
                                        )}
                                        {equipment}
                                    </div>
                                ))}
                            </>
                        )}

                        {openFilter === 'quantity' && (
                            <>
                                <div 
                                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${quantityFilter === 'all' ? 'bg-gray-50' : ''}`}
                                    onClick={() => {
                                        setQuantityFilter('all')
                                        setOpenFilter(null)
                                    }}
                                >
                                    All Quantities
                                </div>

                                {quantities.map(quantity => (
                                    <div
                                        key={quantity}
                                        className={`px-4 py-2 cursor-pointer hover:bg-gray-100 ${quantityFilter === quantity ? 'bg-gray-50' : ''}`}
                                        onClick={() => {
                                            setQuantityFilter(quantity)
                                            setOpenFilter(null)
                                        }}
                                    >
                                        {quantity}
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className="flex gap-4 items-center">
                <div className="w-32">
                    <SearchInput 
                        placeholder="ID"
                        value={idFilter}
                        onChange={(e) => setIdFilter(e.target.value)}
                        className="w-full"
                    />
                </div>
                
                <div className='flex-grow'>
                    <SearchInput 
                        placeholder="Exercise Name"
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                    />
                </div>
                
                {/* Status Dropdowns */}
                <div className="flex gap-4">
                    <div className="relative w-56">
                        <select
                            value={customFilter === 'custom' ? 'custom' : excludeFromGeneratorFilter}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === 'custom') {
                                    setCustomFilter('custom');
                                    setExcludeFromGeneratorFilter('all');
                                } else {
                                    setCustomFilter('all');
                                    setExcludeFromGeneratorFilter(value);
                                }
                            }}
                            className="w-full rounded-md py-2 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="all">All</option>
                            <option value="0">Include in Generator</option>
                            <option value="1">Exclude from Generator</option>
                            <option value="custom">Custom Exercises</option>
                        </select>
                    </div>
                    <div className="relative w-48">
                        <select
                            value={archivedFilter}
                            onChange={(e) => setArchivedFilter(Number(e.target.value))}
                            className="w-full rounded-md py-2 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-orange-500"
                        >
                            <option value={0}>Active</option>
                            <option value={1}>Archived</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Active Filters Summary */}
            <div className="flex items-center justify-between text-sm">
                <div className="text-gray-500">
                    <span className="font-medium">
                        {(nameFilter || typeFilter !== 'all' || categoryFilter !== 'all' || 
                            locationFilter !== 'all' || lateralFilter !== 'all' || 
                            equipmentFilter !== 'all' || quantityFilter !== 'all' || 
                            customFilter !== 'all') ? (
                            <>
                                Filters Applied: 
                                {nameFilter && <span className="ml-2">Name: "{nameFilter}"</span>}
                                {typeFilter !== 'all' && <span className="ml-2">Type: {typeFilter}</span>}
                                {categoryFilter !== 'all' && <span className="ml-2">Category: {categoryFilter}</span>}
                                {locationFilter !== 'all' && <span className="ml-2">Location: {locationFilter}</span>}
                                {lateralFilter !== 'all' && <span className="ml-2">Lateral: {lateralFilter}</span>}
                                {equipmentFilter === 'none' && <span className="ml-2">Equipment: No Equipment</span>}
                                {equipmentFilter !== 'all' && equipmentFilter !== 'none' && (
                                    <span className="ml-2">Equipment: {equipmentFilter}</span>
                                )}
                                {quantityFilter !== 'all' && <span className="ml-2">Quantity: {quantityFilter}</span>}
                                {customFilter === 'custom' && <span className="ml-2">Custom Exercises</span>}
                            </>
                        ) : (
                            'No Filters Applied'
                        )}
                    </span>
                </div>
                <div className="text-gray-500">
                    {filteredExercises.length} {filteredExercises.length === 1 ? 'exercise' : 'exercises'} found
                </div>
            </div>

            {exercises.length === 0 && <MediumLoadingComponent />}

            {exercises.length > 0 && (
                <div className="text-sm border border-gray-400 divide-y divide-gray-400 rounded-lg overflow-hidden bg-white">
                    {/* Table Header */}
                    <div className="grid grid-cols-47 bg-gray-200 font-semibold divide-x divide-gray-400 text-xs">
                        {/* ID Column */}
                        <div className="col-span-2 px-2 py-2">
                            <div 
                                className="flex items-center justify-center gap-1 cursor-pointer hover:bg-gray-300 rounded px-1"
                                onClick={() => handleSort('id')}
                            >
                                Id
                                {sortConfig.key === 'id' && (
                                    sortConfig.direction === 'asc' 
                                        ? <ChevronUpIcon className="h-4 w-4" />
                                        : <ChevronDownIcon className="h-4 w-4" />
                                )}
                            </div>
                        </div>

                        {/* Name Column */}
                        <div className="col-span-17 px-2 py-2">
                            <div 
                                className="flex items-center gap-1 cursor-pointer hover:bg-gray-300 rounded px-1"
                                onClick={() => handleSort('name')}
                            >
                                Name
                                {sortConfig.key === 'name' && (
                                    sortConfig.direction === 'asc' 
                                        ? <ChevronUpIcon className="h-4 w-4" />
                                        : <ChevronDownIcon className="h-4 w-4" />
                                )}
                            </div>
                        </div>

                        {/* Progression Level Column */}
                        <div className="col-span-8 px-2 py-2">
                            <div 
                                className="flex items-center gap-1 cursor-pointer hover:bg-gray-300 rounded px-1"
                                onClick={() => handleSort('progressionLevel')}
                            >
                                <span>Progression Level</span>
                                {sortConfig.key === 'progressionLevel' && (
                                    sortConfig.direction === 'asc' 
                                        ? <ChevronUpIcon className="h-4 w-4" />
                                        : <ChevronDownIcon className="h-4 w-4" />
                                )}
                            </div>
                        </div>

                        {/* Type Column */}
                        <div className="col-span-4 px-2 py-2">
                            <div className="flex items-center justify-between">
                                <span>Type</span>
                                <div className="relative">
                                    <Cog6ToothIcon 
                                        className="h-4 w-4 cursor-pointer hover:text-gray-600"
                                        onClick={(e) => handleFilterClick('type', e)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Category Column */}
                        <div className="col-span-4 px-2 py-2">
                            <div className="flex items-center justify-between">
                                <span>Category</span>
                                <div className="relative">
                                    <Cog6ToothIcon 
                                        className="h-4 w-4 cursor-pointer hover:text-gray-600"
                                        onClick={(e) => handleFilterClick('category', e)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Location Column */}
                        <div className="col-span-3 px-2 py-2">
                            <div className="flex items-center justify-between">
                                <span>Location</span>
                                <div className="relative">
                                    <Cog6ToothIcon 
                                        className="h-4 w-4 cursor-pointer hover:text-gray-600"
                                        onClick={(e) => handleFilterClick('location', e)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Lateral Column */}
                        <div className="col-span-3 px-2 py-2">
                            <div className="flex items-center justify-between">
                                <span>Lateral</span>
                                <div className="relative">
                                    <Cog6ToothIcon 
                                        className="h-4 w-4 cursor-pointer hover:text-gray-600"
                                        onClick={(e) => handleFilterClick('lateral', e)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Equipment Column */}
                        <div className="col-span-3 px-2 py-2">
                            <div className="flex items-center justify-between">
                                <span>Equipment</span>
                                <div className="relative">
                                    <Cog6ToothIcon 
                                        className="h-4 w-4 cursor-pointer hover:text-gray-600"
                                        onClick={(e) => handleFilterClick('equipment', e)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Quantity Column */}
                        <div className="col-span-3 px-2 py-2">
                            <div className="flex items-center justify-between">
                                <span>Quantity</span>
                                <div className="relative">
                                    <Cog6ToothIcon 
                                        className="h-4 w-4 cursor-pointer hover:text-gray-600"
                                        onClick={(e) => handleFilterClick('quantity', e)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table Body */}
                    {filteredExercises.length === 0 ? (
                        <div className="px-4 py-3 text-gray-500 text-center">No exercises found</div>
                    ) : (
                        filteredExercises.map((exercise) => (
                            <div
                                key={exercise.id}
                                className="grid grid-cols-47 hover:bg-gray-50 cursor-pointer divide-x divide-gray-400"
                                onClick={() => OnExerciseClicked(exercise)}
                            >
                                <div className="col-span-2 px-2 py-1 text-center flex items-center justify-center">{exercise.id}</div>
                                <div className="col-span-17 px-2 py-1 truncate flex items-center gap-x-2">
                                    <img
                                        className="w-6 h-6 rounded-full object-cover border border-gray-200"
                                        src={exercise.imageUrl || 'https://which-weight-public.s3.us-east-2.amazonaws.com/equipment_icons/dumbbell.png'}
                                        alt=""
                                    />
                                    <div className="truncate flex-grow">{exercise.name}</div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            OnExerciseNameCopyButtonClicked(exercise.name);
                                        }}
                                        className="p-1 hover:bg-gray-100 rounded-full"
                                        title="Copy exercise name"
                                    >
                                        <DocumentDuplicateIcon className="h-4 w-4 text-gray-500" />
                                    </button>
                                </div>
                                <div className={`col-span-8 flex items-center justify-between px-2 py-1 truncate ${GetProgressionLevelColor(exercise.joinedProgressionLevel)}`}>
                                    <span>{exercise.joinedProgressionLevel}</span>
                                    <span className="mr-2 text-xs font-semibold">{GetProgressionLevelName(exercise.joinedProgressionLevel[0])}</span>
                                </div>
                                <div className="col-span-4 px-2 py-1 truncate flex items-center">{exercise.exerciseType}</div>
                                <div className="col-span-4 px-2 py-1 truncate flex items-center">{exercise.category}</div>
                                <div className="col-span-3 px-2 py-1 truncate flex items-center">{exercise.location}</div>
                                <div className="col-span-3 px-2 py-1 truncate flex items-center">{exercise.lateral}</div>
                                <div className="col-span-3 px-2 py-1 truncate flex items-center gap-x-1 overflow-hidden" title={exercise.equipment}>
                                    {exercise.equipment.split(',').map((equipment, index) => {
                                        const key = equipment.trim().toLowerCase().replace(/\s+/g, '-')
                                        const media = equipmentMedia[key]
                                        if (!media) return null
                                        return (
                                            <img
                                                key={index}
                                                className="w-5 h-5 object-contain flex-shrink-0"
                                                src={media.imageURL}
                                                alt={media.name}
                                                title={media.name}
                                            />
                                        )
                                    })}
                                </div>
                                <div className="col-span-3 px-2 py-1 truncate flex items-center">{exercise.equipmentQuantity}</div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </ScreenFrame>
    )
}

// Reusable search input component
const SearchInput = ({ placeholder, value, onChange, className = '' }) => (
    <div className={`relative flex items-stretch focus-within:z-10 ${className}`}>
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
            type="text"
            className={`w-full rounded-md py-2 pl-10 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 ${className}`}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
        />
        {value && (
            <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3"
                onClick={() => onChange({ target: { value: '' } })}
            >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
            </button>
        )}
    </div>
)